import { byPrefixAndName } from '@awesome.me/kit-f16c5d7bca/icons'
import {
  Alert,
  AlertIcon,
  AlertDescription,
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  Link,
  Input,
  Stack,
  Text,
  AlertTitle,
  InputGroup,
  InputRightElement,
  FormHelperText
} from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { Link as NavLink, useNavigate } from 'react-router-dom-v7'
import { REACT_APP_ENV } from '../../Constants'
import { loginWithCredentials, AUTH_ACTIONS } from '../../redux/storeConfig/slices/slice.auth'
import { validators } from '../../utility/Utils'
import Auth0Button from './Auth0Button'
import MFAInput from './MFAInput'

export default function SessionNew() {
  /** @type {import('../../redux/storeConfig/slices/slice.auth/auth.types').AuthSlice} */
  const { status, task, errors, devToken } = useSelector(state => state.auth)
  const dispatch = useDispatch()

  const tokenRequired = useMemo(() => errors.find(e => e.action === 'token_required'), [errors])

  const navigate = useNavigate()

  const [formData, setFormData] = useState({ email: '', password: '' }) // 2FA token
  const [tokenResent, setTokenResent] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'idle' })
  }, [dispatch])

  useEffect(() => {
    if (tokenRequired) navigate('/login?token_required=true')
    else navigate('/login')
  }, [navigate, tokenRequired])

  const { register, handleSubmit, formState } = useForm({ mode: 'onSubmit' })

  const onSubmit = useCallback(
    async data => {
      setFormData(data)
      dispatch(loginWithCredentials(data))
    },
    [dispatch]
  )

  const resendToken = useCallback(async () => {
    if (tokenRequired && !tokenResent) {
      dispatch(loginWithCredentials(formData))

      setTokenResent(true)
    }
  }, [dispatch, formData, tokenRequired, tokenResent])

  if (tokenRequired)
    return (
      <Stack spacing="4" alignItems="center">
        {devToken && (
          <Alert status="info">
            <AlertIcon />
            <AlertTitle>Dev Token recieved: </AlertTitle>
            <AlertDescription>{REACT_APP_ENV === 'development' && devToken}</AlertDescription>
          </Alert>
        )}
        <MFAInput formData={formData} />
        <Stack spacing={2}>
          {tokenRequired.message.split('\n').map((m, i) => (
            <Text key={i} textAlign="center" mb={0}>
              {m}
            </Text>
          ))}
        </Stack>
        {!tokenResent && (
          <Button type="button" variant="link" colorScheme="secondary" size="sm" onClick={resendToken}>
            Didn&apos;t receive a code?
          </Button>
        )}
      </Stack>
    )

  return (
    <>
      <Stack spacing="6">
        <Stack as="form" spacing="4" onSubmit={handleSubmit(onSubmit)} noValidate>
          <Alert hidden={task !== 'login' || status !== 'failed' || !errors.length} status="error">
            <AlertIcon />
            <AlertDescription>{errors[0]?.message}</AlertDescription>
          </Alert>
          <FormControl isRequired isInvalid={formState.errors.email}>
            <Input
              autoFocus
              type="email"
              placeholder="Enter your email"
              {...register('email', {
                validate: validators.email
              })}
            />
            <FormErrorMessage>{formState.errors?.email?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isRequired isInvalid={formState.errors.password}>
            <InputGroup>
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                {...register('password', { validate: validators.password })}
              />
              <InputRightElement cursor="pointer" onClick={() => setShowPassword(prev => !prev)}>
                <FontAwesomeIcon size="sm" icon={byPrefixAndName['fad'][showPassword ? 'eye-slash' : 'eye']} />
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{formState.errors?.password?.message}</FormErrorMessage>
            <FormHelperText>
              <Link as={NavLink} to="/forgot_password">
                Forgot Your Password?
              </Link>
            </FormHelperText>
          </FormControl>
          <Button type="submit" isLoading={status === 'loading'}>
            Continue with email
          </Button>
        </Stack>
        <Divider />
        <Auth0Button service="google" />
        <Auth0Button service="microsoft" />{' '}
      </Stack>
      <Text mb="0" size="sm">
        Don&apos;t have an account?{' '}
        <Link isExternal href="https://www.marketfuel.com/contact">
          Contact us.
        </Link>
      </Text>
      <Text size="xs" color="neutral.500" textAlign="center">
        By continuing, you acknowledge that you have read, understood, and agree to our{' '}
        <Link isExternal href="https://www.marketfuel.com/terms">
          terms and conditions
        </Link>{' '}
        and{' '}
        <Link isExternal href="https://www.marketfuel.com/privacy">
          privacy policy
        </Link>
      </Text>
    </>
  )
}

export { SessionNew }
